import { DateField } from "@refinedev/antd";
import { useGetIdentity, useTranslate } from "@refinedev/core";
import { useAdmin, useTuner } from "../../../hooks/auth";
import { Avatar, Button, Empty, Popover, Spin, Typography, Upload, theme, Tooltip } from "antd";
import TextArea from "antd/es/input/TextArea";
import { useEffect, useRef, useState } from "react";
import { useAvatarPalette } from "../../../shared/styles";
import { fullName, getInitials, getAvatarColor } from "../../../shared/utils";
import { DownloadOutlined, PaperClipOutlined, SendOutlined, FileOutlined, UnlockOutlined } from "@ant-design/icons";

// Type definitions
interface IUser {
    id: number;
    firstName?: string;
    lastName?: string;
    photo?: { path: string } | null;
}

interface IAttachment {
    id: string;
    file: { path: string };
    isInternal?: boolean;
    name?: string;
}

interface IMessage {
    id: number;
    content: string;
    sender?: IUser;
    createdAt: string;
    attachments?: IAttachment[];
}

// Stable key for list items so they don't remount when parent state (e.g. draft) changes
const getMessageKey = (message: IMessage, index: number) =>
    `${message.createdAt}-${index}`;

const getAttachmentName = (path: string): string => {
    try {
        const u = new URL(path);
        return decodeURIComponent(u.pathname.split("/").pop() || path);
    } catch {
        return path.split("/").pop() || path;
    }
};

const MessageBubble = ({
    message,
    isOwn,
    token,
    t,
    canViewInternalAttachments,
}: {
    message: IMessage;
    isOwn: boolean;
    token: ReturnType<typeof theme.useToken>["token"];
    t: (key: string) => string;
    canViewInternalAttachments: boolean;
}) => {
    const visibleAttachments =
        message.attachments?.filter((a) => !a.isInternal || canViewInternalAttachments) ?? [];
    const hasAttachments = visibleAttachments.length > 0;
    const hasContent = message.content && message.content.trim().length > 0;

    return (
        <div
            className={`min-w-[200px] max-w-[75%] rounded-2xl px-4 py-3 transition-all duration-200 ${
                isOwn ? "rounded-br-md" : "rounded-bl-md"
            }`}
            style={{
                backgroundColor: isOwn
                    ? `${token.colorPrimary}25`
                    : token.colorBgElevated,
                border: `1px solid ${isOwn ? `${token.colorPrimary}40` : token.colorBorderSecondary}`,
            }}
        >
            {hasContent && (
                <Typography.Text className="whitespace-pre-wrap break-words leading-relaxed">
                    {message.content}
                </Typography.Text>
            )}
            {hasAttachments && (
                <div className={`flex flex-col gap-2 ${hasContent ? "mt-3" : ""}`}>
                    {visibleAttachments.map((attachment: IAttachment) => {
                        const fileName = getAttachmentName(attachment.file.path);
                        return (
                            <Popover
                                key={attachment.id}
                                content={
                                    <Typography.Text className="text-sm font-medium break-all">
                                        {fileName}
                                    </Typography.Text>
                                }
                                trigger="hover"
                                placement={isOwn ? "left" : "right"}
                            >
                                <a
                                    href={attachment.file.path}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-200 hover:opacity-80 no-underline"
                                    style={{
                                        backgroundColor: isOwn
                                            ? `${token.colorPrimary}30`
                                            : token.colorBgContainer,
                                        border: `1px solid ${isOwn ? `${token.colorPrimary}40` : token.colorBorderSecondary}`,
                                        color: token.colorPrimary,
                                    }}
                                >
                                    {attachment.name?.includes('decrypted') ?
                                        <UnlockOutlined style={{ fontSize: 16 }} />
                                    : <FileOutlined style={{ fontSize: 16 }} />}

                                    <span className="text-sm font-medium flex-1 truncate" title={fileName}>
                                        {t("attachments.attachment")}
                                    </span>
                                    <DownloadOutlined style={{ fontSize: 14, opacity: 0.7 }} />
                                </a>
                            </Popover>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const MessageRow = ({
    message,
    index,
    currentUserId,
    token,
    t,
    canViewInternalAttachments,
    avatarPalette,
}: {
    message: IMessage;
    index: number;
    currentUserId?: number;
    token: ReturnType<typeof theme.useToken>["token"];
    t: (key: string) => string;
    canViewInternalAttachments: boolean;
    avatarPalette: string[];
}) => {
    const isOwn = message.sender?.id === currentUserId;
    const showAvatar = true;

    return (
        <div
            className={`flex pt-2 gap-3 mb-4 animate-fadeIn ${isOwn ? "flex-row-reverse" : "flex-row"}`}
            style={{ animationDelay: `${index * 50}ms` }}
        >
            {showAvatar && (
                <div className="flex-shrink-0 pt-1">
                    <Tooltip title={fullName(message.sender)} placement={isOwn ? "left" : "right"}>
                        <Avatar
                            size={36}
                            src={message.sender?.photo?.path}
                            className="shadow-sm transition-transform duration-200 hover:scale-105"
                            style={{
                                backgroundColor: message.sender?.photo?.path
                                    ? undefined
                                    : getAvatarColor(message.sender?.id || 0, avatarPalette),
                                fontWeight: 600,
                                fontSize: 14,
                            }}
                        >
                            {message.sender?.photo?.path
                                ? null
                                : message.sender ? getInitials(message.sender?.firstName, message.sender?.lastName) : "C"}
                        </Avatar>
                    </Tooltip>
                </div>
            )}

            <div className={`flex flex-col ${isOwn ? "items-end" : "items-start"}`}>
                <Typography.Text
                    type="secondary"
                    className="text-xs mb-1.5 px-1 font-medium"
                >
                    {message.sender ? fullName(message.sender) : t("messages.fields.client")}
                </Typography.Text>

                <MessageBubble
                    message={message}
                    isOwn={isOwn}
                    token={token}
                    t={t}
                    canViewInternalAttachments={canViewInternalAttachments}
                />

                <div className="mt-1.5 px-1">
                    <DateField
                        value={message.createdAt}
                        format="DD/MM/YY HH:mm"
                        className="text-[11px] opacity-60"
                    />
                </div>
            </div>
        </div>
    );
};

type MessagesProps = {
    messages: IMessage[];
    fileUploadProps: any;
    sendMessage: (message: string) => void;
    fileList: any;
    maxHeight?: number;
    hasMore?: boolean;
    isLoadingInitial?: boolean;
    isLoadingMore?: boolean;
    loadOlder?: () => void;
};

export const Messages = ({
    messages,
    fileUploadProps,
    sendMessage,
    fileList,
    maxHeight = 1000,
    hasMore = false,
    isLoadingInitial = false,
    isLoadingMore = false,
    loadOlder,
}: MessagesProps) => {

    const _t = useTranslate();
    const { token } = theme.useToken();
    const avatarPalette = useAvatarPalette();
    const [message, setMessage] = useState("");
    const user = useGetIdentity<IUser>();
    const isAdmin = useAdmin();
    const isTuner = useTuner();
    const canViewInternalAttachments = isAdmin || isTuner;
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const hasAppliedInitialScrollRef = useRef(false);
    const isPrependingRef = useRef(false);
    const previousScrollHeightRef = useRef(0);

    const scrollToBottom = () => {
        if (containerRef.current) {
            containerRef.current.scrollTop = containerRef.current.scrollHeight;
        }
    };

    useEffect(() => {
        if (!containerRef.current) {
            return;
        }
        if (isPrependingRef.current) {
            const currentContainer = containerRef.current;
            const delta = currentContainer.scrollHeight - previousScrollHeightRef.current;
            currentContainer.scrollTop = delta + currentContainer.scrollTop;
            isPrependingRef.current = false;
            return;
        }
        if (!hasAppliedInitialScrollRef.current && messages.length > 0) {
            scrollToBottom();
            hasAppliedInitialScrollRef.current = true;
        }
    }, [messages]);

    const onSendMessage = () => {
        if (message.trim()) {
            sendMessage(message);
            setMessage("");
            inputRef.current?.focus();
            scrollToBottom();
        }
    };

    const handleScroll = () => {
        if (!containerRef.current || !loadOlder || !hasMore || isLoadingMore) {
            return;
        }
        const threshold = 80;
        if (containerRef.current.scrollTop <= threshold) {
            previousScrollHeightRef.current = containerRef.current.scrollHeight;
            isPrependingRef.current = true;
            loadOlder();
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            onSendMessage();
        }
    };

    const EmptyState = () => (
        <div className="flex flex-col items-center justify-center py-12 px-4">
            <div
                className="w-16 h-16 rounded-full flex items-center justify-center"
                style={{ backgroundColor: `${token.colorPrimary}15` }}
            >
                <SendOutlined
                    style={{ fontSize: 24, color: token.colorPrimary, opacity: 0.6 }}
                    rotate={-45}
                />
            </div>
        </div>
    );

    return (
        <div className="flex flex-col h-full">
            {/* Messages container */}
            <div
                ref={containerRef}
                onScroll={handleScroll}
                className="flex-1 overflow-y-auto overflow-x-hidden px-4 py-4 scroll-smooth"
                style={{
                    maxHeight: `${maxHeight - 120}px`,
                    minHeight: '200px',
                }}
            >
                {isLoadingInitial ? (
                    <div className="flex items-center justify-center py-6">
                        <Spin size="small" />
                    </div>
                ) : messages?.length > 0 ? (
                    <div className="space-y-1">
                        {isLoadingMore && (
                            <div className="flex items-center justify-center pb-2">
                                <Typography.Text type="secondary" className="text-xs">
                                    {_t("messages.loadingOlder")}
                                </Typography.Text>
                            </div>
                        )}
                        {messages.map((msg: IMessage, index: number) => (
                            <MessageRow
                                key={msg.id ?? getMessageKey(msg, index)}
                                message={msg}
                                index={index}
                                currentUserId={user?.data?.id}
                                token={token}
                                t={_t}
                                canViewInternalAttachments={canViewInternalAttachments}
                                avatarPalette={avatarPalette}
                            />
                        ))}
                    </div>
                ) : (
                    <EmptyState />
                )}
            </div>

            {/* Input area */}
            <div
                className="px-4 py-3 border-t"
                style={{
                    borderColor: token.colorBorderSecondary,
                }}
            >
                {/* Text input with integrated actions */}
                <div
                    className="flex items-end gap-2 p-2 rounded-xl transition-all duration-200"
                    style={{
                        backgroundColor: token.colorBgContainer,
                        border: `1px solid ${token.colorBorder}`,
                    }}
                >
                    {/* Attachment button */}
                    <Upload
                        {...fileUploadProps}
                        showUploadList={false}
                        fileList={fileList}
                    >
                        <Tooltip title={_t('orders.titles.attachments')}>
                            <Button
                                type="text"
                                icon={<PaperClipOutlined style={{ fontSize: 18 }} />}
                                className="flex items-center justify-center opacity-60 hover:opacity-100 transition-opacity"
                                style={{
                                    width: 36,
                                    height: 36,
                                    color: token.colorTextSecondary,
                                }}
                            />
                        </Tooltip>
                    </Upload>

                    {/* Text area */}
                    <TextArea
                        ref={inputRef}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={_t('messages.typeMessage')}
                        autoSize={{ minRows: 1, maxRows: 4 }}
                        className="flex-1 border-none shadow-none focus:shadow-none resize-none"
                        style={{
                            backgroundColor: 'transparent',
                            padding: '8px 4px',
                        }}
                    />

                    {/* Send button */}
                    <Tooltip title={_t('orders.titles.sendMessage')}>
                        <Button
                            type="primary"
                            icon={<SendOutlined rotate={-45} />}
                            onClick={onSendMessage}
                            disabled={!message.trim() && (!fileList || fileList.length === 0)}
                            className="flex items-center justify-center transition-all duration-200"
                            style={{
                                width: 36,
                                height: 36,
                                borderRadius: 10,
                            }}
                        />
                    </Tooltip>
                </div>

                {/* File list display */}
                {fileList && fileList.length > 0 && (
                    <div className="mt-2 px-2">
                        <Upload
                            {...fileUploadProps}
                            showUploadList={{
                                showPreviewIcon: false,
                                showRemoveIcon: true,
                            }}
                            fileList={fileList}
                            listType="text"
                        />
                    </div>
                )}
            </div>

            {/* CSS for animations */}
            <style>{`
                @keyframes fadeIn {
                    from {
                        opacity: 0;
                        transform: translateY(10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                .animate-fadeIn {
                    animation: fadeIn 0.3s ease-out forwards;
                }
                .ant-input:focus,
                .ant-input-focused {
                    box-shadow: none !important;
                }
            `}</style>
        </div>
    );
};